import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ChevronRight, Code2, ServerCog, Database, ArrowDown, ExternalLink, Radio } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";
import { displayReqNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import StatusPill from "@/components/StatusPill";

function MethodBadge({ method }) {
  const colors = {
    POST: "bg-green-100 text-green-800 border-green-200",
    PATCH: "bg-amber-100 text-amber-800 border-amber-200",
    PUT: "bg-blue-100 text-blue-800 border-blue-200",
    DELETE: "bg-red-100 text-red-800 border-red-200",
    GET: "bg-slate-100 text-slate-800 border-slate-200",
  };
  return <span className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded border ${colors[method] || colors.GET}`}>{method}</span>;
}

function copy(text) {
  navigator.clipboard.writeText(text).then(
    () => toast.success("Copied to clipboard"),
    () => toast.error("Copy failed")
  );
}

function LifecycleStep({ step, idx, provider }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={`step-${provider}-${step.step}`}>
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CollapsibleTrigger className="w-full text-left">
          <div className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
            <div className="w-8 h-8 rounded-md bg-[#0033A0] text-white grid place-items-center font-bold text-sm shrink-0">{step.step}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <MethodBadge method={step.method} />
                <div className="font-display font-bold text-slate-900">{step.title}</div>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{step.when} · <span className="font-mono">{step.url}</span></div>
            </div>
            <ChevronRight size={16} className={`text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 border-t border-slate-200 bg-slate-50/50 grid lg:grid-cols-2 gap-4 pt-4">
            {/* Request */}
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Request URL</div>
                <div className="mt-1 font-mono text-xs bg-slate-900 text-emerald-300 rounded-md px-3 py-2 break-all flex items-start justify-between gap-2">
                  <span>{step.method} {step.url}</span>
                  <button onClick={() => copy(`${step.method} ${step.url}`)} className="opacity-70 hover:opacity-100 shrink-0"><Copy size={12} /></button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Headers</div>
                <pre className="mt-1 font-mono text-[11px] bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto">{JSON.stringify(step.headers, null, 2)}</pre>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Request Body</div>
                  <Button size="sm" variant="outline" onClick={() => copy(JSON.stringify(step.body, null, 2))} data-testid={`copy-req-${provider}-${step.step}`}>
                    <Copy size={12} className="mr-1.5" /> Copy
                  </Button>
                </div>
                <pre className="mt-1 font-mono text-[11px] bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto max-h-96">{JSON.stringify(step.body, null, 2)}</pre>
              </div>
            </div>

            {/* Response */}
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Simulated Response</div>
                <div className="mt-1 font-mono text-xs rounded-md px-3 py-2 bg-slate-900 text-slate-100">
                  <span className={`inline-block px-2 py-0.5 rounded font-bold ${String(step.response.status).startsWith("2") ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                    {step.response.status}
                  </span> <span className="text-slate-400">{String(step.response.status).startsWith("2") ? "OK" : "Error"}</span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Response Body</div>
                  <Button size="sm" variant="outline" onClick={() => copy(JSON.stringify(step.response.body, null, 2))} data-testid={`copy-res-${provider}-${step.step}`}>
                    <Copy size={12} className="mr-1.5" /> Copy
                  </Button>
                </div>
                <pre className="mt-1 font-mono text-[11px] bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto max-h-72">{JSON.stringify(step.response.body, null, 2)}</pre>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default function Integrations() {
  const { persona } = usePersona();
  const { brand } = useBranding();
  const [tickets, setTickets] = useState([]);
  const [ticketId, setTicketId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectors, setConnectors] = useState(null);
  const [presenterMode] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("flexspace.presenterMode") !== "off";
  });

  useEffect(() => {
    endpoints.tickets({ persona: persona.id }).then((t) => {
      setTickets(t);
      if (t.length > 0) setTicketId((prev) => prev || t[0].id);
      setLoading(false);
    });
    endpoints.connectorHealth().then(setConnectors).catch(() => {});
  }, [persona.id]);

  useEffect(() => {
    if (!ticketId) return;
    setData(null);
    endpoints.itsmLifecycle(ticketId).then(setData).catch(() => setData(null));
  }, [ticketId]);

  const selectedTicket = tickets.find((t) => t.id === ticketId);
  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
            <Code2 size={12} style={{ color: brandBlue }} /> Integrations
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">Connector Health</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Which data sources Flexspace is wired to — and which ones are still on sandbox data.
            Every connector here maps 1:1 to a source referenced in the six-agent story.
          </p>
        </div>
        {connectors && (
          <div className="text-right shrink-0">
            <div className="text-3xl font-semibold" style={{ color: brandBlue }}>
              {connectors.live_count}<span className="text-slate-400 text-lg"> / {connectors.total}</span>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">connectors live</div>
          </div>
        )}
      </div>

      <Tabs defaultValue="connectors" data-testid="integrations-top-tabs">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="connectors" data-testid="tab-connectors" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
            Connectors
          </TabsTrigger>
          <TabsTrigger value="story" data-testid="tab-story" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
            Integration Story · ITSM
          </TabsTrigger>
        </TabsList>

        {/* Primary tab · Connector Health board */}
        <TabsContent value="connectors" className="space-y-6 mt-4" data-testid="tab-panel-connectors">
          {!connectors && <Skeleton className="h-64 w-full" />}
          {connectors && (
            <ConnectorHealthBoard connectors={connectors} presenter={presenterMode} brandBlue={brandBlue} brandSoft={brandSoft} />
          )}
        </TabsContent>

        {/* Secondary tab · existing ITSM ticket lifecycle viewer */}
        <TabsContent value="story" className="space-y-6 mt-4" data-testid="tab-panel-story">
          <div className="text-sm text-slate-600 max-w-2xl">
            The exact sequence of REST API calls Flexspace makes to your ITSM system across a ticket&apos;s lifecycle.
            Pick a ticket to see the full request/response payloads for <strong>ServiceNow</strong>, <strong>BigFix</strong>, <strong>Nexthink</strong> and <strong>OEM Factory</strong>.
          </div>

      <Card className="p-5 border-slate-200 shadow-sm">
        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Choose a Flexspace ticket</div>
            <Select value={ticketId || ""} onValueChange={setTicketId}>
              <SelectTrigger data-testid="ticket-picker" className="mt-2 w-full">
                <SelectValue placeholder="Select ticket…" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {tickets.map((t) => {
                  const dn = displayReqNumber(t);
                  return (
                  <SelectItem key={t.id} value={t.id} data-testid={`ticket-opt-${dn}`}>
                    <span className="font-mono text-xs mr-2">{dn}</span>
                    {t.subject} <span className="text-slate-400 ml-2">· {t.status}</span>
                  </SelectItem>
                );})}
              </SelectContent>
            </Select>
          </div>
          {selectedTicket && (
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <StatusPill status={selectedTicket.status} />
              <a href={`/tickets/${selectedTicket.id}`} className="text-[#0033A0] hover:underline inline-flex items-center gap-1">
                Open ticket <ExternalLink size={12} />
              </a>
            </div>
          )}
        </div>
      </Card>

      {loading && <Skeleton className="h-64 w-full" />}

      {data && (
        <>
          <div className="grid md:grid-cols-4 gap-4" data-testid="integration-summary">
            <Card className="p-5 border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <ServerCog size={14} /> ServiceNow
              </div>
              <div className="mt-2 font-display font-black text-3xl text-slate-900">{data.lifecycle.servicenow.length}</div>
              <div className="text-xs text-slate-500">sc_request lifecycle</div>
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <ServerCog size={14} /> BigFix
              </div>
              <div className="mt-2 font-display font-black text-3xl text-slate-900">{data.lifecycle.bigfix.length}</div>
              <div className="text-xs text-slate-500">Service Management</div>
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <ServerCog size={14} /> Nexthink
              </div>
              <div className="mt-2 font-display font-black text-3xl text-slate-900">{(data.lifecycle.nexthink || []).length}</div>
              <div className="text-xs text-slate-500">Device UX + self-heal</div>
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <ServerCog size={14} /> OEM Factory
              </div>
              <div className="mt-2 font-display font-black text-3xl text-slate-900">{(data.lifecycle.oem || []).length}</div>
              <div className="text-xs text-slate-500">Purchase → ship → deliver</div>
            </Card>
          </div>

          <Tabs defaultValue="servicenow" data-testid="integration-tabs">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="servicenow" data-testid="tab-servicenow" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
                ServiceNow ({data.lifecycle.servicenow.length})
              </TabsTrigger>
              <TabsTrigger value="bigfix" data-testid="tab-bigfix" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
                BigFix Service Mgmt ({data.lifecycle.bigfix.length})
              </TabsTrigger>
              <TabsTrigger value="nexthink" data-testid="tab-nexthink" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
                Nexthink Device UX ({(data.lifecycle.nexthink || []).length})
              </TabsTrigger>
              <TabsTrigger value="oem" data-testid="tab-oem" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
                OEM Factory ({(data.lifecycle.oem || []).length})
              </TabsTrigger>
            </TabsList>

            {["servicenow", "bigfix", "nexthink", "oem"].map((provider) => (
              <TabsContent key={provider} value={provider} className="space-y-3 mt-4" data-testid={`lifecycle-${provider}`}>
                {(data.lifecycle[provider] || []).map((step, i) => (
                  <div key={step.step}>
                    <LifecycleStep step={step} idx={i} provider={provider} />
                    {i < (data.lifecycle[provider] || []).length - 1 && (
                      <div className="flex justify-center py-1">
                        <ArrowDown size={14} className="text-slate-300" />
                      </div>
                    )}
                  </div>
                ))}
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}

      {!loading && !data && tickets.length === 0 && (
        <Card className="p-10 text-center border-slate-200 shadow-sm">
          <div className="text-sm text-slate-500">Place an order first to generate a ticket for the demo.</div>
        </Card>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Connector Health board ----------

const STAGE_ORDER = ["Plan", "Supply", "Fulfilment", "Device", "Refresh", "Recovery", "Cross-cutting"];

function ConnectorHealthBoard({ connectors, presenter, brandBlue, brandSoft }) {
  const buckets = connectors.buckets || {};
  const stages = STAGE_ORDER.filter((s) => (buckets[s] || []).length > 0);

  return (
    <div className="space-y-8" data-testid="connector-health-board">
      {stages.map((stage) => (
        <div key={stage}>
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-3">
            {stage} · {buckets[stage].length} connector{buckets[stage].length !== 1 ? "s" : ""}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {buckets[stage].map((c) => (
              <ConnectorCard key={c.id} connector={c} presenter={presenter} brandBlue={brandBlue} brandSoft={brandSoft} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ConnectorCard({ connector, presenter, brandBlue, brandSoft }) {
  const c = connector;
  const targetHref = c.stage_id ? `/lifecycle?stage=${c.stage_id}` : "/lifecycle";
  return (
    <a
      href={targetHref}
      className="rounded-2xl bg-white p-5 block hover:shadow-sm transition-shadow"
      style={{ border: `1px solid ${brandSoft}` }}
      data-testid={`connector-card-${c.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-xl grid place-items-center text-sm font-bold shrink-0"
          style={{ backgroundColor: brandSoft, color: brandBlue }}
        >
          {c.monogram}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
          <div className="text-xs text-slate-500 truncate mt-0.5">{c.role}</div>
        </div>
        <StatusChip live={c.live} warning={c.warning} presenter={presenter} />
      </div>

      <div className="text-[11px] text-slate-500 leading-5 min-h-[36px]">
        {c.reason}
      </div>

      {presenter && !c.live && c.env_keys?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-1">Requires</div>
          <div className="flex flex-wrap gap-1">
            {c.env_keys.map((k) => (
              <span key={k} className="text-[10px] font-mono bg-slate-50 text-slate-600 rounded px-1.5 py-0.5">{k}</span>
            ))}
          </div>
        </div>
      )}
    </a>
  );
}

function StatusChip({ live, warning, presenter }) {
  if (live && warning) {
    return (
      <span
        className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
        style={{ backgroundColor: "#FEF3C7", color: "#B45309" }}
        title="Connected but with a warning"
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#B45309" }} />
        Live · warning
      </span>
    );
  }
  if (live) {
    return (
      <span
        className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
        style={{ backgroundColor: "#ECFDF5", color: "#0F766E" }}
      >
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#0F766E" }} />
        Live
      </span>
    );
  }
  if (!presenter) {
    return (
      <span
        className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap text-slate-400"
        style={{ backgroundColor: "#F1F5F9" }}
      >
        Pending
      </span>
    );
  }
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
      style={{ backgroundColor: "#FEF3C7", color: "#B45309" }}
    >
      Sandbox
    </span>
  );
}
